import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";

const ABILITIES = ["str", "dex", "con", "int", "wis", "cha"];
const ABILITY_LABELS = { str: "Strength", dex: "Dexterity", con: "Constitution", int: "Intelligence", wis: "Wisdom", cha: "Charisma" };

// A single ability boost raises the score by 2, unless it's already 18+ (then just +1) — the same
// math applies whether the boost comes from ancestry/background/class/free, or a level-up milestone.
export const applyBoost = (abilities, key) => ({ ...abilities, [key]: abilities[key] + (abilities[key] >= 18 ? 1 : 2) });
export const applyFlaw = (abilities, key) => ({ ...abilities, [key]: Math.max(1, abilities[key] - 2) });

function AbilityPicker({ options, excluded, onPick, label }) {
  return (
    <div className="space-y-1.5">
      {label && <p className="text-[10px] uppercase text-[#f1e0c5]/40">{label}</p>}
      <div className="grid grid-cols-3 gap-2">
        {options.map((a) => (
          <button
            key={a}
            type="button"
            data-testid={`boost-pick-${a}`}
            disabled={excluded.includes(a)}
            onClick={() => onPick(a)}
            className={`text-xs px-2 py-2 rounded-lg border transition-colors duration-200 ${
              excluded.includes(a) ? "border-white/5 text-[#f1e0c5]/20 cursor-not-allowed" : "border-white/10 hover:border-[#D4AF37] hover:text-[#D4AF37]"
            }`}
          >
            {ABILITY_LABELS[a]}
          </button>
        ))}
      </div>
    </div>
  );
}

export function ScoreTable({ abilities }) {
  return (
    <div className="grid grid-cols-3 gap-2 bg-black/20 border border-white/10 rounded-xl p-3">
      {ABILITIES.map((a) => (
        <div key={a} className="text-center">
          <p className="text-[9px] uppercase text-[#f1e0c5]/40">{a}</p>
          <p className="text-lg text-[#D4AF37]" data-testid={`boost-running-score-${a}`}>{abilities[a]}</p>
        </div>
      ))}
    </div>
  );
}

/**
 * The N-free-boosts step (each boost MUST go to a different ability this step) — reused both as
 * the final step of full character creation, and standalone for the 5th/10th/15th/20th level PF2e
 * milestone (where you gain 4 more free boosts with no source-specific fixed picks).
 */
export function FreeBoostsStep({ abilities, count, onDone }) {
  const [remaining, setRemaining] = useState(count);
  const [used, setUsed] = useState([]);
  const [running, setRunning] = useState(abilities);

  const pick = (key) => {
    const next = applyBoost(running, key);
    setRunning(next);
    const nextUsed = [...used, key];
    setUsed(nextUsed);
    if (remaining - 1 <= 0) onDone(next, nextUsed);
    else setRemaining(remaining - 1);
  };

  return (
    <div className="space-y-3">
      <p className="text-sm text-[#f1e0c5]/70">Pick {remaining} more free ability boost{remaining === 1 ? "" : "s"} — each must go to a different ability.</p>
      <ScoreTable abilities={running} />
      <AbilityPicker options={ABILITIES} excluded={used} onPick={pick} />
    </div>
  );
}

// Steps: ancestry-fixed -> ancestry-free -> background-fixed -> background-free -> class -> free -> review
export default function AbilityBoostWizardPf2e({ open, onOpenChange, ancestry, background, klass, onComplete }) {
  const [phase, setPhase] = useState("ancestry-fixed");
  const [abilities, setAbilities] = useState({ str: 10, dex: 10, con: 10, int: 10, wis: 10, cha: 10 });
  const [history, setHistory] = useState([]);

  const hasAncestry = !!ancestry;
  const freeAncestryBoosts = hasAncestry ? ancestry.free_boosts || 0 : 2; // custom/no ancestry: fall back to the "2 free boosts instead" errata option
  const hasBackground = !!background;
  const bgOptions = hasBackground ? background.ability_boosts || [] : ABILITIES;
  const keyAbilityOptions = klass?.key_ability?.length ? klass.key_ability : ABILITIES;

  useEffect(() => {
    if (!open) return;
    setAbilities({ str: 10, dex: 10, con: 10, int: 10, wis: 10, cha: 10 });
    setHistory([]);
    setPhase(hasAncestry ? "ancestry-fixed" : freeAncestryBoosts > 0 ? "ancestry-free" : "background-fixed");
  }, [open]);

  // The dialog portal itself won't mount while closed, but this component's own render function
  // still runs on every parent re-render — without this guard, the very first render (before the
  // effect above ever fires) evaluates the "ancestry-fixed" JSX with a still-null `ancestry` prop
  // and crashes on `ancestry.name`.
  if (!open) return null;

  const PHASE_LABEL = {
    "ancestry-fixed": "Ancestry", "ancestry-free": "Ancestry", "background-fixed": "Background",
    "background-free": "Background", "class": "Class", "free": "Free Boosts", "review": "Review",
  };
  const PHASE_ORDER = ["ancestry-fixed", "ancestry-free", "background-fixed", "background-free", "class", "free", "review"];

  const addHistory = (entries) => setHistory((h) => [...h, ...entries]);

  const finish = (finalAbilities) => {
    onComplete(finalAbilities, history);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#322216] border-white/10 text-[#f1e0c5] max-w-md max-h-[85vh] overflow-y-auto scrollbar-thin" data-testid="ability-boost-wizard-pf2e">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Ability Boosts — {PHASE_LABEL[phase]}</DialogTitle>
          <DialogDescription>Step {PHASE_ORDER.indexOf(phase) + 1} of {PHASE_ORDER.length}</DialogDescription>
        </DialogHeader>
        <ScoreTable abilities={abilities} />

        {phase === "ancestry-fixed" && (
          <div className="space-y-3">
            <p className="text-sm text-[#f1e0c5]/70">
              {ancestry.name} grants boosts to <strong className="text-[#D4AF37]">{(ancestry.ability_boosts || []).map((a) => ABILITY_LABELS[a]).join(" and ")}</strong>
              {ancestry.ability_flaw?.length ? <> and a flaw to <strong className="text-[#D90429]">{ancestry.ability_flaw.map((a) => ABILITY_LABELS[a]).join(", ")}</strong></> : null}.
            </p>
            <Button
              data-testid="ancestry-apply-fixed-button"
              className="w-full rounded-full bg-[#D90429] hover:bg-[#b8031f]"
              onClick={() => {
                let next = abilities;
                const entries = [];
                for (const a of ancestry.ability_boosts || []) {
                  entries.push({ source: "ancestry", ability: a, amount: next[a] >= 18 ? 1 : 2 });
                  next = applyBoost(next, a);
                }
                for (const a of ancestry.ability_flaw || []) {
                  entries.push({ source: "ancestry_flaw", ability: a, amount: -2 });
                  next = applyFlaw(next, a);
                }
                setAbilities(next);
                addHistory(entries);
                setPhase(freeAncestryBoosts > 0 ? "ancestry-free" : "background-fixed");
              }}
            >
              Apply {ancestry.name}'s Boosts
            </Button>
          </div>
        )}

        {phase === "ancestry-free" && (
          <>
            {!hasAncestry && <p className="text-sm text-[#f1e0c5]/70">No ancestry selected (custom) — pick {freeAncestryBoosts} free boosts to any abilities instead.</p>}
            <FreeBoostsStep
              abilities={abilities}
              count={freeAncestryBoosts}
              onDone={(next, picked) => {
                setAbilities(next);
                addHistory(picked.map((a) => ({ source: "ancestry_free", ability: a, amount: 2 })));
                setPhase("background-fixed");
              }}
            />
          </>
        )}

        {phase === "background-fixed" && (
          <div className="space-y-3">
            <p className="text-sm text-[#f1e0c5]/70">
              {hasBackground ? `${background.name} lets you pick ONE of two boosts, plus one fully free boost.` : "No background selected (custom) — pick any ability for the fixed boost, plus one fully free boost."}
            </p>
            <AbilityPicker
              label="Choose your background's fixed boost"
              options={bgOptions}
              excluded={[]}
              onPick={(a) => {
                setAbilities(applyBoost(abilities, a));
                addHistory([{ source: "background", ability: a, amount: 2 }]);
                setPhase("background-free");
              }}
            />
          </div>
        )}

        {phase === "background-free" && (
          <FreeBoostsStep
            abilities={abilities}
            count={1}
            onDone={(next, picked) => {
              setAbilities(next);
              addHistory(picked.map((a) => ({ source: "background_free", ability: a, amount: 2 })));
              setPhase("class");
            }}
          />
        )}

        {phase === "class" && (
          <div className="space-y-3">
            <p className="text-sm text-[#f1e0c5]/70">Your class's key ability gets a boost.</p>
            <AbilityPicker
              options={keyAbilityOptions}
              excluded={[]}
              onPick={(a) => {
                setAbilities(applyBoost(abilities, a));
                addHistory([{ source: "class", ability: a, amount: 2 }]);
                setPhase("free");
              }}
            />
          </div>
        )}

        {phase === "free" && (
          <FreeBoostsStep
            abilities={abilities}
            count={4}
            onDone={(next, picked) => {
              setAbilities(next);
              addHistory(picked.map((a) => ({ source: "free", ability: a, amount: 2 })));
              setPhase("review");
            }}
          />
        )}

        {phase === "review" && (
          <div className="space-y-3">
            <p className="text-sm text-[#f1e0c5]/70">Final ability scores — review, then confirm.</p>
            <Button data-testid="ability-boost-confirm-button" className="w-full rounded-full bg-[#D90429] hover:bg-[#b8031f]" onClick={() => finish(abilities)}>Confirm & Use These Scores</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
